import { collection, getDocs } from "firebase/firestore";
import writeXlsxFile from "write-excel-file";
import { db } from "../../../../shared/config/firebase-config";
import {
  apiPathCompanyLevel,
  apiPathScorecardLevel,
} from "../../../../shared/apis/AppApi";

type DataRow = Record<string, any>;

export type ScoreSummary = {
  objectives: number;
  measures: number;
  totalWeight: number;
  midtermScore: number | null;
  finalScore: number | null;
  targetAchievement: number | null;
  measuresWithActuals: number;
  completion: number;
};

export type ComputedScorecardExport = {
  generatedAt: string;
  formatVersion: 2;
  financialYears: DataRow[];
};

export type OrganizedScorecardRow = {
  financialYear: string;
  recordType: string;
  uid: string;
  name: string;
  department: string;
  section: string;
  strategicGoal: string;
  objective: string;
  measure: string;
  perspective: string;
  reviewStatus: string;
  midtermScore: number | null;
  finalScore: number | null;
  targetAchievement: number | null;
  completion: number | null;
};

const SCORECARD_COLLECTIONS = [
  "strategicThemes",
  "companyObjectives",
  "companyMeasures",
  "objectives",
  "measures",
  "scorecardMetadata",
  "departmentObjectives",
  "departmentMeasures",
  "divisionObjectives",
  "divisionMeasures",
] as const;

const serializeValue = (value: any): any => {
  if (value === null || value === undefined || typeof value !== "object") return value;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(serializeValue);
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, serializeValue(child)]));
};

const readCollection = async (path: string): Promise<DataRow[]> => {
  const snapshot = await getDocs(collection(db, path));
  return snapshot.docs.map((item) => ({
    id: item.id,
    ...serializeValue(item.data()),
  }));
};

const text = (...values: any[]) => {
  const value = values.find((item) => item !== null && item !== undefined && String(item).trim());
  return value === undefined ? "" : String(value);
};

const number = (...values: any[]): number | null => {
  const value = values.find((item) => item !== null && item !== undefined && item !== "");
  if (value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round = (value: number | null, digits = 2) =>
  value === null ? null : Number(value.toFixed(digits));

const average = (values: number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

const getId = (row: DataRow) => text(row.id, row.uid);

const matches = (value: any, ...candidates: any[]) => {
  const normalized = text(value).toLocaleLowerCase();
  return Boolean(normalized) && candidates.some((candidate) => text(candidate).toLocaleLowerCase() === normalized);
};

const measureRatings = (measure: DataRow) => ({
  midtermEmployee: number(measure.autoRating, measure.q2EmployeeRating),
  midtermSupervisor: number(measure.supervisorRating, measure.q2supervisorRating, measure.q2SupervisorRating),
  midtermFinal: number(measure.finalRating, measure.q2FinalRating),
  finalEmployee: number(measure.autoRating2, measure.q4EmployeeRating),
  finalSupervisor: number(measure.supervisorRating2, measure.q4supervisorRating, measure.q4SupervisorRating),
  final: number(measure.finalRating2, measure.q4FinalRating),
});

const computedSummary = (objectives: DataRow[], measures: DataRow[]): ScoreSummary => {
  const weightedScore = (stage: "midtermFinal" | "final") => {
    const rated = measures
      .map((measure) => ({ rating: measureRatings(measure)[stage], weight: number(measure.weight) || 0 }))
      .filter((item) => item.rating !== null && item.weight > 0) as Array<{ rating: number; weight: number }>;
    const weight = rated.reduce((sum, item) => sum + item.weight, 0);
    return weight ? rated.reduce((sum, item) => sum + item.rating * item.weight, 0) / weight : null;
  };

  const achievements = measures
    .map((measure) => {
      const target = number(measure.annualTarget);
      const actual = number(measure.annualActual, measure.quarter4Actual);
      return target !== null && target !== 0 && actual !== null ? (actual / target) * 100 : null;
    })
    .filter((value): value is number => value !== null && Number.isFinite(value));
  const measuresWithActuals = measures.filter(
    (measure) => number(measure.annualActual, measure.quarter4Actual, measure.midtermActual, measure.quarter2Actual) !== null
  ).length;

  return {
    objectives: objectives.length,
    measures: measures.length,
    totalWeight: round(measures.reduce((sum, measure) => sum + (number(measure.weight) || 0), 0)) || 0,
    midtermScore: round(weightedScore("midtermFinal")),
    finalScore: round(weightedScore("final")),
    targetAchievement: round(average(achievements)),
    measuresWithActuals,
    completion: measures.length ? round((measuresWithActuals / measures.length) * 100) || 0 : 0,
  };
};

const cleanMeasure = (measure: DataRow) => ({
  id: getId(measure),
  name: text(measure.description, measure.name),
  perspective: text(measure.perspective),
  weight: number(measure.weight),
  dataType: text(measure.dataType),
  unit: text(measure.dataSymbol),
  baseline: number(measure.baseline),
  annualTarget: number(measure.annualTarget),
  annualActual: number(measure.annualActual),
  quarterlyActuals: {
    q1: number(measure.quarter1Actual),
    q2: number(measure.quarter2Actual),
    q3: number(measure.quarter3Actual),
    q4: number(measure.quarter4Actual),
  },
  ratings: measureRatings(measure),
  targetDate: measure.targetDate || null,
  activities: text(measure.activities),
  sourceOfEvidence: text(measure.sourceOfEvidence),
  comments: {
    agreement: text(measure.comments),
    midterm: text(measure.midtermComments),
    final: text(measure.assessmentComments),
  },
});

const buildObjectives = (objectives: DataRow[], measures: DataRow[]) =>
  objectives.map((objective) => {
    const objectiveMeasures = measures.filter((measure) => matches(measure.objective, objective.id));
    return {
      id: getId(objective),
      name: text(objective.description, objective.name),
      perspective: text(objective.perspective),
      weight: number(objective.weight),
      strategicGoalId: text(objective.theme),
      parentObjectiveId: text(objective.parent),
      type: text(objective.objectiveType, "performance"),
      score: computedSummary([objective], objectiveMeasures),
      measures: objectiveMeasures.map(cleanMeasure),
    };
  });

const scorecardStatus = (scorecard: DataRow) => {
  if (scorecard.active) return "Active";
  if (scorecard.current) return "Current";
  if (scorecard.locked) return "Locked";
  return "Inactive";
};

const reviewStatus = (metadata: DataRow | undefined) => ({
  agreement: text(metadata?.agreementDraft?.status, metadata?.draftReview?.status, "Not started"),
  midterm: text(metadata?.quarter2Review?.status, metadata?.midtermReview?.status, "Not started"),
  final: text(metadata?.quarter4Review?.status, metadata?.finalAssessment?.status, "Not started"),
});

export const buildScorecardExport = async (): Promise<ComputedScorecardExport> => {
  const [scorecards, users, departments, divisions, businessUnits] = await Promise.all([
    readCollection(apiPathCompanyLevel("scorecards")),
    readCollection(apiPathCompanyLevel("users")),
    readCollection(apiPathCompanyLevel("departments")),
    readCollection(apiPathCompanyLevel("divisions")),
    readCollection(apiPathCompanyLevel("businessUnits")),
  ]);

  const financialYears = await Promise.all(
    scorecards
      .sort((a, b) => text(b.description, b.id).localeCompare(text(a.description, a.id)))
      .map(async (scorecard) => {
        const yearId = getId(scorecard);
        const entries = await Promise.all(
          SCORECARD_COLLECTIONS.map(async (name) => [
            name,
            await readCollection(apiPathScorecardLevel(yearId, name)),
          ] as const)
        );
        const data = Object.fromEntries(entries) as Record<(typeof SCORECARD_COLLECTIONS)[number], DataRow[]>;

        const companyObjectives = buildObjectives(data.companyObjectives, data.companyMeasures);
        const companySummary = computedSummary(data.companyObjectives, data.companyMeasures);
        const strategicGoals = data.strategicThemes.map((goal) => {
          const goalObjectives = companyObjectives.filter((objective) =>
            matches(objective.strategicGoalId, goal.id, goal.description, goal.name)
          );
          return {
            id: getId(goal),
            name: text(goal.description, goal.name),
            score: computedSummary(
              data.companyObjectives.filter((objective) => goalObjectives.some((item) => item.id === getId(objective))),
              data.companyMeasures.filter((measure) =>
                goalObjectives.some((objective) => objective.measures.some((item: DataRow) => item.id === getId(measure)))
              )
            ),
            objectives: goalObjectives,
          };
        });
        const assignedCompanyObjectiveIds = new Set(
          strategicGoals.flatMap((goal) => goal.objectives.map((objective) => objective.id))
        );

        const people = Object.fromEntries(users.map((user) => {
          const uid = text(user.uid, user.id);
          const userObjectives = data.objectives.filter((objective) => matches(objective.uid, uid));
          const userMeasures = data.measures.filter((measure) => matches(measure.uid, uid));
          const department = departments.find((item) => matches(item.id, user.department));
          const metadata = data.scorecardMetadata.find((item) => matches(item.uid, item.id, uid));
          return [uid, {
            uid,
            name: text(user.displayName, `${text(user.firstName)} ${text(user.lastName)}`),
            email: text(user.email),
            jobTitle: text(user.jobTitle),
            role: text(user.role),
            department: { id: text(user.department), name: text(department?.name, department?.description) },
            supervisorUid: text(user.supervisor),
            reviewStatus: reviewStatus(metadata),
            summary: computedSummary(userObjectives, userMeasures),
            objectives: buildObjectives(userObjectives, userMeasures),
          }];
        }));

        const departmentExports = departments.map((department) => {
          const departmentId = getId(department);
          const division = divisions.find((item) => matches(item.id, department.division));
          const departmentObjectives = data.departmentObjectives.filter((objective) =>
            matches(objective.department, objective.uid, departmentId)
          );
          const departmentMeasures = data.departmentMeasures.filter((measure) =>
            matches(measure.department, measure.uid, departmentId)
          );
          const memberUids = users
            .filter((user) => matches(user.department, departmentId))
            .map((user) => text(user.uid, user.id));
          const employeeObjectives = data.objectives.filter((objective) => memberUids.includes(text(objective.uid)));
          const employeeMeasures = data.measures.filter((measure) => memberUids.includes(text(measure.uid)));
          return {
            id: departmentId,
            name: text(department.name, department.description),
            division: { id: text(department.division), name: text(division?.name, division?.description) },
            memberUids,
            employeeRollup: computedSummary(employeeObjectives, employeeMeasures),
            departmentScorecard: {
              summary: computedSummary(departmentObjectives, departmentMeasures),
              objectives: buildObjectives(departmentObjectives, departmentMeasures),
            },
          };
        });

        const divisionExports = divisions.map((division) => {
          const divisionId = getId(division);
          const businessUnit = businessUnits.find((item) => matches(item.id, division.businessUnit));
          const divisionObjectives = data.divisionObjectives.filter((objective) =>
            matches(objective.division, objective.uid, divisionId)
          );
          const divisionMeasures = data.divisionMeasures.filter((measure) =>
            matches(measure.division, measure.uid, divisionId)
          );
          return {
            id: divisionId,
            name: text(division.name, division.description),
            businessUnit: { id: text(division.businessUnit), name: text(businessUnit?.name, businessUnit?.description) },
            departmentIds: departments.filter((item) => matches(item.division, divisionId)).map(getId),
            summary: computedSummary(divisionObjectives, divisionMeasures),
            objectives: buildObjectives(divisionObjectives, divisionMeasures),
          };
        });

        return {
          financialYear: {
            id: yearId,
            label: text(scorecard.description, scorecard.name, yearId),
            status: scorecardStatus(scorecard),
            locked: Boolean(scorecard.locked),
          },
          overview: {
            people: users.length,
            departments: departments.length,
            divisions: divisions.length,
            company: companySummary,
            employeeScorecards: computedSummary(data.objectives, data.measures),
          },
          company: {
            summary: companySummary,
            strategicGoals,
            unassignedObjectives: companyObjectives.filter((objective) => !assignedCompanyObjectiveIds.has(objective.id)),
          },
          divisions: divisionExports,
          departments: departmentExports,
          people,
        };
      })
  );

  return { generatedAt: new Date().toISOString(), formatVersion: 2, financialYears };
};

export const createOrganizedScorecardRows = (
  data: ComputedScorecardExport
): OrganizedScorecardRow[] => {
  const rows: OrganizedScorecardRow[] = [];
  const achievement = (measure: DataRow) => {
    const target = number(measure.annualTarget);
    const actual = number(measure.annualActual);
    return target !== null && target !== 0 && actual !== null ? round((actual / target) * 100) : null;
  };
  const makeRow = (values: Partial<OrganizedScorecardRow>): OrganizedScorecardRow => ({
    financialYear: "",
    recordType: "",
    uid: "",
    name: "",
    department: "",
    section: "",
    strategicGoal: "",
    objective: "",
    measure: "",
    perspective: "",
    reviewStatus: "",
    midtermScore: null,
    finalScore: null,
    targetAchievement: null,
    completion: null,
    ...values,
  });

  data.financialYears.forEach((year) => {
    const financialYear = text(year.financialYear.label);
    const goalNames = new Map<string, string>();
    year.company.strategicGoals.forEach((goal: DataRow) => goalNames.set(text(goal.id), text(goal.name)));

    rows.push(makeRow({
      financialYear,
      recordType: "Company Scorecard",
      uid: "company",
      name: "Company",
      reviewStatus: text(year.financialYear.status),
      midtermScore: year.company.summary.midtermScore,
      finalScore: year.company.summary.finalScore,
      targetAchievement: year.company.summary.targetAchievement,
      completion: year.company.summary.completion,
    }));

    year.company.strategicGoals.forEach((goal: DataRow) => {
      rows.push(makeRow({ financialYear, recordType: "Strategic Goal", uid: "company", name: goal.name,
        strategicGoal: goal.name, midtermScore: goal.score.midtermScore, finalScore: goal.score.finalScore,
        targetAchievement: goal.score.targetAchievement, completion: goal.score.completion }));
      goal.objectives.forEach((objective: DataRow) => {
        rows.push(makeRow({ financialYear, recordType: "Company Objective", uid: "company", name: objective.name,
          strategicGoal: goal.name, objective: objective.name, perspective: objective.perspective,
          midtermScore: objective.score.midtermScore, finalScore: objective.score.finalScore,
          targetAchievement: objective.score.targetAchievement, completion: objective.score.completion }));
        objective.measures.forEach((measure: DataRow) => rows.push(makeRow({ financialYear,
          recordType: "Company Measure", uid: "company", name: measure.name, strategicGoal: goal.name,
          objective: objective.name, measure: measure.name, perspective: measure.perspective,
          midtermScore: measure.ratings.midtermFinal, finalScore: measure.ratings.final,
          targetAchievement: achievement(measure),
          completion: measure.annualActual !== null ? 100 : 0 })));
      });
    });

    year.company.unassignedObjectives.forEach((objective: DataRow) => {
      rows.push(makeRow({ financialYear, recordType: "Company Objective", uid: "company", name: objective.name,
        objective: objective.name, perspective: objective.perspective, midtermScore: objective.score.midtermScore,
        finalScore: objective.score.finalScore, targetAchievement: objective.score.targetAchievement,
        completion: objective.score.completion }));
      objective.measures.forEach((measure: DataRow) => rows.push(makeRow({ financialYear,
        recordType: "Company Measure", uid: "company", name: measure.name, objective: objective.name,
        measure: measure.name, perspective: measure.perspective, midtermScore: measure.ratings.midtermFinal,
        finalScore: measure.ratings.final, targetAchievement: achievement(measure),
        completion: measure.annualActual !== null ? 100 : 0 })));
    });

    year.departments.forEach((department: DataRow) => {
      rows.push(makeRow({ financialYear, recordType: "Department", uid: department.id, name: department.name,
        department: department.name, section: department.division.name,
        midtermScore: department.employeeRollup.midtermScore, finalScore: department.employeeRollup.finalScore,
        targetAchievement: department.employeeRollup.targetAchievement, completion: department.employeeRollup.completion }));
      department.departmentScorecard.objectives.forEach((objective: DataRow) => {
        rows.push(makeRow({ financialYear, recordType: "Department Objective", uid: department.id,
          name: department.name, department: department.name, section: department.division.name,
          objective: objective.name, perspective: objective.perspective,
          midtermScore: objective.score.midtermScore, finalScore: objective.score.finalScore,
          targetAchievement: objective.score.targetAchievement, completion: objective.score.completion }));
        objective.measures.forEach((measure: DataRow) => rows.push(makeRow({ financialYear,
          recordType: "Department Measure", uid: department.id, name: department.name,
          department: department.name, section: department.division.name, objective: objective.name,
          measure: measure.name, perspective: measure.perspective, midtermScore: measure.ratings.midtermFinal,
          finalScore: measure.ratings.final, targetAchievement: achievement(measure),
          completion: measure.annualActual !== null ? 100 : 0 })));
      });
    });

    year.divisions.forEach((division: DataRow) => {
      rows.push(makeRow({ financialYear, recordType: "Section", uid: division.id, name: division.name,
        section: division.name, midtermScore: division.summary.midtermScore, finalScore: division.summary.finalScore,
        targetAchievement: division.summary.targetAchievement, completion: division.summary.completion }));
      division.objectives.forEach((objective: DataRow) => {
        rows.push(makeRow({ financialYear, recordType: "Section Objective", uid: division.id,
          name: division.name, section: division.name, objective: objective.name,
          perspective: objective.perspective, midtermScore: objective.score.midtermScore,
          finalScore: objective.score.finalScore, targetAchievement: objective.score.targetAchievement,
          completion: objective.score.completion }));
        objective.measures.forEach((measure: DataRow) => rows.push(makeRow({ financialYear,
          recordType: "Section Measure", uid: division.id, name: division.name, section: division.name,
          objective: objective.name, measure: measure.name, perspective: measure.perspective,
          midtermScore: measure.ratings.midtermFinal, finalScore: measure.ratings.final,
          targetAchievement: achievement(measure), completion: measure.annualActual !== null ? 100 : 0 })));
      });
    });

    Object.values(year.people).forEach((person: any) => {
      const section = year.departments.find((item: DataRow) => item.id === person.department.id)?.division?.name || "";
      rows.push(makeRow({ financialYear, recordType: "User", uid: person.uid, name: person.name,
        department: person.department.name, section,
        reviewStatus: `Agreement: ${person.reviewStatus.agreement}; Midterm: ${person.reviewStatus.midterm}; Final: ${person.reviewStatus.final}`,
        midtermScore: person.summary.midtermScore, finalScore: person.summary.finalScore,
        targetAchievement: person.summary.targetAchievement, completion: person.summary.completion }));
      person.objectives.forEach((objective: DataRow) => {
        const strategicGoal = goalNames.get(text(objective.strategicGoalId)) || text(objective.strategicGoalId);
        rows.push(makeRow({ financialYear, recordType: "User Objective", uid: person.uid, name: person.name,
          department: person.department.name, section, strategicGoal, objective: objective.name,
          perspective: objective.perspective, midtermScore: objective.score.midtermScore,
          finalScore: objective.score.finalScore, targetAchievement: objective.score.targetAchievement,
          completion: objective.score.completion }));
        objective.measures.forEach((measure: DataRow) => rows.push(makeRow({ financialYear,
          recordType: "User Measure", uid: person.uid, name: person.name, department: person.department.name,
          section, strategicGoal, objective: objective.name, measure: measure.name,
          perspective: measure.perspective, midtermScore: measure.ratings.midtermFinal,
          finalScore: measure.ratings.final,
          targetAchievement: achievement(measure),
          completion: measure.annualActual !== null ? 100 : 0 })));
      });
    });
  });

  return rows;
};

export const loadOrganizedScorecardRows = async () =>
  createOrganizedScorecardRows(await buildScorecardExport());

const downloadBlob = (blob: Blob, fileName: string) => {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportDatabaseJson = async () => {
  const data = await buildScorecardExport();
  downloadBlob(
    new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" }),
    `scorecards-all-financial-years-${new Date().toISOString().slice(0, 10)}.json`
  );
};

const cell = (value: any, header = false) => ({
  value: value ?? "",
  type: typeof value === "number" ? Number : typeof value === "boolean" ? Boolean : String,
  fontWeight: header ? "bold" : undefined,
  backgroundColor: header ? "#DCE6F1" : undefined,
  wrap: true,
});

const sheet = (rows: DataRow[]) => {
  const safeRows = rows.length ? rows : [{ note: "No data found" }];
  const headers = Array.from(new Set(safeRows.flatMap((row) => Object.keys(row))));
  return [headers.map((header) => cell(header, true)), ...safeRows.map((row) => headers.map((header) => cell(row[header])))];
};

const summaryColumns = (summary: ScoreSummary, prefix = "") => ({
  [`${prefix}Objectives`]: summary.objectives,
  [`${prefix}Measures`]: summary.measures,
  [`${prefix}Weight`]: summary.totalWeight,
  [`${prefix}Midterm Score`]: summary.midtermScore,
  [`${prefix}Final Score`]: summary.finalScore,
  [`${prefix}Target Achievement %`]: summary.targetAchievement,
  [`${prefix}Completion %`]: summary.completion,
});

export const exportDatabaseExcel = async () => {
  const data = await buildScorecardExport();
  const overview: DataRow[] = [];
  const goals: DataRow[] = [];
  const departments: DataRow[] = [];
  const employees: DataRow[] = [];
  const objectives: DataRow[] = [];
  const measures: DataRow[] = [];

  const addObjectiveRows = (
    fy: string,
    level: string,
    ownerId: string,
    owner: string,
    items: DataRow[]
  ) => items.forEach((objective) => {
    objectives.push({ "Financial Year": fy, Level: level, "Owner ID": ownerId, Owner: owner,
      "Objective ID": objective.id, Objective: objective.name, Perspective: objective.perspective,
      Weight: objective.weight, "Strategic Goal ID": objective.strategicGoalId, ...summaryColumns(objective.score) });
    objective.measures.forEach((measure: DataRow) => measures.push({ "Financial Year": fy, Level: level,
      "Owner ID": ownerId, Owner: owner, "Objective ID": objective.id, Objective: objective.name,
      "Measure ID": measure.id, Measure: measure.name, Perspective: measure.perspective, Weight: measure.weight,
      Baseline: measure.baseline, "Annual Target": measure.annualTarget, "Annual Actual": measure.annualActual,
      "Q1 Actual": measure.quarterlyActuals.q1, "Q2 Actual": measure.quarterlyActuals.q2,
      "Q3 Actual": measure.quarterlyActuals.q3, "Q4 Actual": measure.quarterlyActuals.q4,
      "Midterm Final Rating": measure.ratings.midtermFinal, "Final Rating": measure.ratings.final }));
  });

  data.financialYears.forEach((year) => {
    const fy = year.financialYear.label;
    overview.push({ "Financial Year": fy, Status: year.financialYear.status, People: year.overview.people,
      Departments: year.overview.departments, Divisions: year.overview.divisions,
      ...summaryColumns(year.overview.company, "Company "), ...summaryColumns(year.overview.employeeScorecards, "Employees ") });
    year.company.strategicGoals.forEach((goal: DataRow) => goals.push({ "Financial Year": fy,
      "Goal ID": goal.id, "Strategic Goal": goal.name, ...summaryColumns(goal.score) }));
    year.departments.forEach((department: DataRow) => {
      departments.push({ "Financial Year": fy,
        "Department ID": department.id, Department: department.name, Division: department.division.name,
        "Member UIDs": department.memberUids.join(", "), ...summaryColumns(department.departmentScorecard.summary, "Department "),
        ...summaryColumns(department.employeeRollup, "Employee Rollup ") });
      addObjectiveRows(fy, "Department", department.id, department.name, department.departmentScorecard.objectives);
    });
    year.divisions.forEach((division: DataRow) =>
      addObjectiveRows(fy, "Section", division.id, division.name, division.objectives)
    );
    Object.values(year.people).forEach((person: any) => {
      employees.push({ "Financial Year": fy, UID: person.uid, Employee: person.name, Email: person.email,
        "Job Title": person.jobTitle, Department: person.department.name, "Supervisor UID": person.supervisorUid,
        "Agreement Status": person.reviewStatus.agreement, "Midterm Status": person.reviewStatus.midterm,
        "Final Status": person.reviewStatus.final, ...summaryColumns(person.summary) });
      person.objectives.forEach((objective: DataRow) => {
        objectives.push({ "Financial Year": fy, Level: "Employee", "Owner ID": person.uid, Owner: person.name,
          "Objective ID": objective.id, Objective: objective.name, Perspective: objective.perspective,
          Weight: objective.weight, "Strategic Goal ID": objective.strategicGoalId, ...summaryColumns(objective.score) });
        objective.measures.forEach((measure: DataRow) => measures.push({ "Financial Year": fy, Level: "Employee",
          "Owner ID": person.uid, Owner: person.name, "Objective ID": objective.id, Objective: objective.name,
          "Measure ID": measure.id, Measure: measure.name, Perspective: measure.perspective, Weight: measure.weight,
          Baseline: measure.baseline, "Annual Target": measure.annualTarget, "Annual Actual": measure.annualActual,
          "Q1 Actual": measure.quarterlyActuals.q1, "Q2 Actual": measure.quarterlyActuals.q2,
          "Q3 Actual": measure.quarterlyActuals.q3, "Q4 Actual": measure.quarterlyActuals.q4,
          "Midterm Final Rating": measure.ratings.midtermFinal, "Final Rating": measure.ratings.final }));
      });
    });
    year.company.strategicGoals.flatMap((goal: DataRow) => goal.objectives).concat(year.company.unassignedObjectives)
      .forEach((objective: DataRow) => {
        objectives.push({ "Financial Year": fy, Level: "Company", "Owner ID": "company", Owner: "Company",
          "Objective ID": objective.id, Objective: objective.name, Perspective: objective.perspective,
          Weight: objective.weight, "Strategic Goal ID": objective.strategicGoalId, ...summaryColumns(objective.score) });
        objective.measures.forEach((measure: DataRow) => measures.push({ "Financial Year": fy, Level: "Company",
          "Owner ID": "company", Owner: "Company", "Objective ID": objective.id, Objective: objective.name,
          "Measure ID": measure.id, Measure: measure.name, Perspective: measure.perspective, Weight: measure.weight,
          Baseline: measure.baseline, "Annual Target": measure.annualTarget, "Annual Actual": measure.annualActual,
          "Q1 Actual": measure.quarterlyActuals.q1, "Q2 Actual": measure.quarterlyActuals.q2,
          "Q3 Actual": measure.quarterlyActuals.q3, "Q4 Actual": measure.quarterlyActuals.q4,
          "Midterm Final Rating": measure.ratings.midtermFinal, "Final Rating": measure.ratings.final }));
      });
  });

  await writeXlsxFile([overview, goals, departments, employees, objectives, measures].map(sheet) as any, {
    sheets: ["Overview", "Company Strategy", "Departments", "Employees", "Objectives", "Measures"],
    fileName: `scorecards-all-financial-years-${new Date().toISOString().slice(0, 10)}.xlsx`,
    fontFamily: "Arial",
    fontSize: 10,
  } as any);
};
